import { For, Show } from 'solid-js'
import type { HubCheckIn } from '../../lib/hub'

/**
 * What the guest is carrying right now — per-item, with the real tag numbers
 * the venue assigned.
 *
 * The old hub showed only a count ("3 items checked in"), which is useless at a
 * counter: the guest needs the tag number to argue for their coat. This is the
 * card that makes the hub answerable to the person standing in the queue.
 */
export default function ActiveCheckInCard(props: {
  activeCheckIns: HubCheckIn[]
  now?: Date
}) {
  const now = () => props.now ?? new Date()

  /** "45 min", "3 h" — how long the coat has been in. */
  const heldFor = (createdAt: string): string => {
    const started = new Date(createdAt).getTime()
    if (Number.isNaN(started)) return ''
    const mins = Math.max(0, Math.floor((now().getTime() - started) / 60000))
    if (mins < 60) return `${mins} min`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours} h ${mins % 60} min`
    return `${Math.floor(hours / 24)} d`
  }

  return (
    <section class="rounded-2xl bg-emerald-500/10 border border-emerald-400/30 p-5">
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-sm font-semibold text-emerald-200">At the cloakroom now</h2>
        <span class="text-xs text-emerald-300/80">
          {props.activeCheckIns.length} {props.activeCheckIns.length === 1 ? 'session' : 'sessions'}
        </span>
      </div>

      <For each={props.activeCheckIns}>
        {(checkIn) => (
          <div class="mb-4 last:mb-0">
            <div class="flex items-baseline justify-between">
              <p class="font-semibold text-white">{checkIn.venueName}</p>
              <Show when={heldFor(checkIn.createdAt)}>
                <span class="text-xs text-white/50">{heldFor(checkIn.createdAt)}</span>
              </Show>
            </div>

            {/* The tag numbers are the whole point: the guest must be able to
                point at the exact item they handed over. */}
            <ul class="mt-2 space-y-1.5">
              <For each={checkIn.items}>
                {(item) => (
                  <li class="flex items-center justify-between rounded-lg bg-black/20 px-3 py-2">
                    <span class="text-sm text-white/90 capitalize">{item.type}</span>
                    <span class="font-mono text-sm font-semibold text-emerald-200">#{item.tagNumber}</span>
                  </li>
                )}
              </For>
            </ul>
          </div>
        )}
      </For>
    </section>
  )
}
